import { z } from "zod/v4";

import type {
  AdSetRow,
  CampaignRow,
  GroupValue,
  ListedAd,
  ListedAds,
  PerformanceRow,
} from "src/pages/ad-studio/insights/performance/types";
import type {
  EntityKind,
  OpenedIn,
} from "src/pages/ad-studio/insights/performance/view/types";
import { Grain } from "@hightouch/lib/ad-studio/insights-malloy/schemas/cube-meta";
import { insightsDateRangeSchema } from "src/pages/ad-studio/insights/shared/dates";

export type InsightsDrawerEntry = Pick<
  PerformanceRow,
  "entityType" | "entityId" | "entityName"
>;

const syncScopeSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("by-sync"), syncId: z.string() }),
  z.object({
    kind: z.literal("by-account"),
    syncId: z.string(),
    accounts: z.tuple([z.string()], z.string()).readonly(),
  }),
]);

type DrawerRow = Pick<
  PerformanceRow,
  "entityType" | "entityId" | "entityName" | "syncId"
>;
/** What the drawer opens on: the row, its list as prev/next siblings, and the listed ads it can show before its own query answers. */
export type OpenedRow = {
  row: DrawerRow;
  siblings: readonly DrawerRow[];
  listed: ListedAds;
};

const toDrawerRow = ({
  entityType,
  entityId,
  entityName,
  syncId,
}: DrawerRow): DrawerRow => ({ entityType, entityId, entityName, syncId });

const toListedAd = ({
  entityType,
  syncId,
  entityId,
  entityName,
  status,
  media,
  creativeCount,
  fatigue,
}: ListedAd): ListedAd => ({
  entityType,
  syncId,
  entityId,
  entityName,
  status,
  media,
  creativeCount,
  fatigue,
});

export const toOpenedAd = ({
  row,
  rows,
}: {
  row: ListedAd;
  rows: readonly ListedAd[];
}): OpenedRow => ({
  row: toDrawerRow(row),
  siblings: rows.map(toDrawerRow),
  listed: Object.fromEntries(
    rows.map((sibling) => [sibling.entityId, toListedAd(sibling)]),
  ),
});

const toOpenedParent = ({
  row,
  rows,
}: {
  row: AdSetRow | CampaignRow;
  rows: readonly (AdSetRow | CampaignRow)[];
}): OpenedRow => ({
  row: toDrawerRow(row),
  siblings: rows.map(toDrawerRow),
  listed: {},
});

export const toOpenedRow = (opened: OpenedIn<EntityKind>): OpenedRow => {
  switch (opened.kind) {
    case "ads": {
      return toOpenedAd({ row: opened.row, rows: opened.list.rows });
    }
    case "ad-sets":
    case "campaigns": {
      return toOpenedParent({ row: opened.row, rows: opened.list.rows });
    }
    default: {
      return opened satisfies never;
    }
  }
};

const conversionEventsSchema = z.record(
  z.string(),
  z.tuple([z.string()], z.string()).readonly(),
);

const tabListSchema = z.object({
  view: z.string(),
  filters: z.array(z.unknown()),
  segments: z.array(z.string()),
  metrics: z.array(z.string()),
});

const drillStateSchema = z.object({
  dates: insightsDateRangeSchema,
  sync: syncScopeSchema,
  conversionEvents: z.union([
    conversionEventsSchema,
    z.literal("meta-defaults"),
  ]),
  list: z.union([tabListSchema, z.literal("view-defaults")]),
});

export type DrillState = z.output<typeof drillStateSchema>;

/** A group spans every sync its list covered; each ad's drilldown narrows to the ad's own sync. */
const groupDrillStateSchema = z.object({
  dates: insightsDateRangeSchema,
  syncs: z.tuple([syncScopeSchema], syncScopeSchema).readonly(),
  conversionEvents: conversionEventsSchema,
  list: tabListSchema,
  order: z.object({ key: z.string(), direction: z.enum(["asc", "desc"]) }),
});

export type GroupDrillState = z.output<typeof groupDrillStateSchema>;

const groupValueSchema: z.ZodType<GroupValue> = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("value"), raw: z.string() }),
  z.object({ kind: z.literal("no-value") }),
]);

const VALUE_PREFIX = "value:";

/** The URL segment naming a group, so a reload reopens it. */
export const groupValueParam = (value: GroupValue): string => {
  switch (value.kind) {
    case "value": {
      return `${VALUE_PREFIX}${value.raw}`;
    }
    case "no-value": {
      return "no-value";
    }
    default: {
      return value satisfies never;
    }
  }
};

export const groupValueParamSchema: z.ZodType<GroupValue, string> = z
  .string()
  .pipe(
    z.union([
      z.literal("no-value").transform((): GroupValue => ({ kind: "no-value" })),
      z
        .string()
        .startsWith(VALUE_PREFIX)
        .transform(
          (param): GroupValue => ({
            kind: "value",
            raw: param.slice(VALUE_PREFIX.length),
          }),
        ),
    ]),
  );

const videoSourceSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("file"), url: z.string() }),
  z.object({
    kind: z.literal("youtube"),
    videoId: z.string(),
    thumbnailUrl: z.string(),
  }),
]);

const mediaItemSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("image"), url: z.string() }),
  z.object({ kind: z.literal("video"), video: videoSourceSchema }),
]);

const creativeSchema = z.object({
  creativeId: z.string(),
  item: mediaItemSchema,
});

const listedAdSchema: z.ZodType<ListedAd> = z.object({
  entityType: z.literal("ad"),
  syncId: z.string(),
  entityId: z.string(),
  entityName: z.string(),
  status: z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("status"), value: z.string() }),
    z.object({ kind: z.literal("no-status") }),
  ]),
  media: z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("media"),
      creatives: z.tuple([creativeSchema], creativeSchema),
    }),
    z.object({ kind: z.literal("no-media") }),
  ]),
  creativeCount: z.number(),
  fatigue: z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("reported"), status: z.string() }),
    z.object({ kind: z.literal("no-fatigue-row") }),
  ]),
});

// History state survives reloads and can come from an older build, so it is
// parsed rather than trusted.
const locationStateSchema = z.object({
  entityName: z.string(),
  siblings: z.array(
    z.object({
      entityType: Grain,
      entityId: z.string(),
      entityName: z.string(),
    }),
  ),
  drill: z.union([drillStateSchema, z.literal("no-drill-state")]),
  listed: z.record(z.string(), listedAdSchema),
});

const groupLocationStateSchema = z.object({
  opened: z.literal("group"),
  siblings: z.array(groupValueSchema),
  drill: groupDrillStateSchema,
});

/** How the drilldown was opened: from a list row, from a group row, or by URL alone. */
export type InsightsLocationState =
  | ({ kind: "from-list" } & z.output<typeof locationStateSchema>)
  | ({ kind: "from-group-list" } & z.output<typeof groupLocationStateSchema>)
  | { kind: "direct" };

/**
 * Route state for opening a row's drilldown. Carries only the fields prev/next
 * needs, so a long list stays small in history state.
 */
export function insightsDrilldownState({
  row,
  siblings,
  drill,
  listed,
}: {
  row: InsightsDrawerEntry;
  siblings: readonly InsightsDrawerEntry[];
  drill: DrillState | "no-drill-state";
  listed: ListedAds;
}): z.input<typeof locationStateSchema> {
  return {
    entityName: row.entityName,
    siblings: siblings.map(({ entityType, entityId, entityName }) => ({
      entityType,
      entityId,
      entityName,
    })),
    drill,
    listed,
  };
}

export function insightsGroupDrilldownState({
  siblings,
  drill,
}: {
  siblings: readonly GroupValue[];
  drill: GroupDrillState;
}): z.input<typeof groupLocationStateSchema> {
  return { opened: "group", siblings: [...siblings], drill };
}

export function parseInsightsLocationState(
  state: unknown,
): InsightsLocationState {
  const group = groupLocationStateSchema.safeParse(state);
  if (group.success) {
    return { kind: "from-group-list", ...group.data };
  }
  const parsed = locationStateSchema.safeParse(state);
  return parsed.success
    ? { kind: "from-list", ...parsed.data }
    : { kind: "direct" };
}
